/**
 * CitationHoverPopover — Wave 2D
 *
 * Wraps an inline citation chip `[N]` with a HoverCard that previews:
 *   - Document name + page / section
 *   - First 280 chars of the cited excerpt
 *   - Relevance score badge (if present)
 *
 * Click on the wrapped child still propagates to the existing
 * CitationModal (Sheet) flow — hover is purely additive.
 *
 * Composition: `HoverCard` from @radix-ui/react-hover-card, already
 * present via `components/ui/hover-card.tsx`.
 *
 * Open delay: 300 ms (per spec §3.3) to avoid flashing on cursor
 * micro-movements.
 */

import { FileText } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export interface CitationHoverPopoverCitation {
  docName: string;
  page: number | null;
  section: string | null;
  text: string;
  score?: number;
}

interface CitationHoverPopoverProps {
  citation: CitationHoverPopoverCitation;
  children: ReactNode;
}

/**
 * Renders `children` (the `[N]` badge button) wrapped in a HoverCard.
 * The HoverCard content is a compact popover showing key citation info.
 * The click handler on `children` is NOT intercepted — it still opens
 * the full CitationModal Sheet.
 */
export function CitationHoverPopover({ citation, children }: CitationHoverPopoverProps) {
  const excerpt = citation.text?.trim().slice(0, 280) ?? '';
  const hasExcerpt = excerpt.length > 0;
  const score = Number.isFinite(citation.score) ? (citation.score! * 100) : null;

  const scoreTier =
    score === null ? null :
    score >= 70 ? 'high' :
    score >= 40 ? 'medium' : 'low';

  const scoreBadgeClass: Record<string, string> = {
    high:   'bg-green-100  text-green-800  dark:bg-green-900/30  dark:text-green-400',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    low:    'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-400',
  };

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        {/* asChild passes the trigger role through to the wrapped button.
            The badge button's own onClick still fires. */}
        <span>{children}</span>
      </HoverCardTrigger>

      <HoverCardContent
        className="w-80 p-3 space-y-2"
        side="top"
        align="center"
        sideOffset={6}
      >
        {/* ── Document header ───────────────────────────────────────── */}
        <div className="flex items-start gap-2">
          <FileText className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground truncate leading-tight">
              {citation.docName}
            </p>
            {(citation.page !== null || citation.section) && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {citation.page !== null && <span>Page {citation.page}</span>}
                {citation.page !== null && citation.section && <span className="mx-1">·</span>}
                {citation.section && (
                  <span className="italic truncate">{citation.section}</span>
                )}
              </p>
            )}
          </div>

          {/* Relevance score badge */}
          {scoreTier && score !== null && (
            <Badge
              variant="outline"
              className={cn(
                'text-[9px] h-4 px-1 shrink-0 border-0',
                scoreBadgeClass[scoreTier],
              )}
            >
              {Math.round(score)}%
            </Badge>
          )}
        </div>

        {/* ── Cited excerpt ─────────────────────────────────────────── */}
        {hasExcerpt && (
          <div className="border-t pt-2">
            <p className="text-[11px] text-muted-foreground leading-[1.6] line-clamp-4">
              {excerpt}
              {citation.text.trim().length > 280 && (
                <span className="text-muted-foreground/60"> …</span>
              )}
            </p>
          </div>
        )}

        {/* ── Click hint ────────────────────────────────────────────── */}
        <p className="text-[9px] text-muted-foreground/60 pt-0.5">
          Click to view full document
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}
